import { DateTimePicker as MuiDateTimePicker } from "@mui/x-date-pickers";
import classNames from "classnames";
import dayjs, { Dayjs } from "dayjs";
import * as React from "react";

import type { DisplayZone } from "@/shared/datetime";

import styles from "./DateRangePicker.module.css";

export interface DateTimePickerProps {
  /** Full ISO 8601, or `null` when the field holds no usable instant. */
  value: string | null;
  /**
   * Fires on every edit. `null` means the field is empty or mid-entry, so a
   * consumer gating a commit can test for it rather than parsing the value.
   */
  onChange: (value: string | null) => void;
  /** @default "Date & time" */
  label?: string;
  /** Marks the field invalid, for an error shown beside it. */
  invalid?: boolean;
  /**
   * The clock the field is read and typed on. Omitted, it is the viewer's
   * local zone. The value stays an absolute instant either way.
   */
  zone?: DisplayZone;
  className?: string;
}

/**
 * Pick a single instant.
 */
export function DateTimePicker({
  value,
  onChange,
  label = "Date & time",
  invalid = false,
  zone,
  className,
}: DateTimePickerProps) {
  const [instant, setInstant] = React.useState<Dayjs | null>(() =>
    value === null ? null : dayjs(value),
  );

  return (
    <MuiDateTimePicker
      className={classNames(invalid && styles.invalid, className)}
      label={label}
      value={instant}
      timezone={zone ?? "default"}
      onChange={(next) => {
        setInstant(next);
        onChange(next !== null && next.isValid() ? next.toISOString() : null);
      }}
      slotProps={{
        textField: { size: "small", fullWidth: true, error: invalid },
      }}
    />
  );
}
